import type { EdiphorPollHistoryDto } from '../types';

interface PollingMonitorProps {
  pollingHistory?: EdiphorPollHistoryDto;
}

export function PollingMonitor({ pollingHistory }: PollingMonitorProps) {
  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case 'DONE':
        return 'text-green-600 dark:text-green-400';
      case 'PENDING':
      case 'SUBMITTED':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'ERROR':
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getTimeSince = (timestamp: string) => {
    const diff = Date.now() - new Date(timestamp).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        Polling Status
      </h2>

      {!pollingHistory ? (
        <p className="text-gray-500 dark:text-gray-400">Loading...</p>
      ) : (
        <div className="space-y-4">
          {pollingHistory.lastPollTime && (
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Last Poll: <span className="font-medium">{getTimeSince(pollingHistory.lastPollTime)}</span>
            </div>
          )}

          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Recent Activity ({pollingHistory.polls.length})
            </h3>
            
            {pollingHistory.polls.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">No polling activity yet</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {pollingHistory.polls.map((poll) => (
                  <div
                    key={`${poll.requestId}-${poll.polledAt}`}
                    className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded text-sm"
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
                        {poll.requestId}
                      </span>
                      <span className={`font-medium ${getStatusColor(poll.status)}`}>
                        {poll.status}
                      </span>
                      {poll.label && (
                        <span className="text-gray-700 dark:text-gray-300">
                          {poll.label}
                        </span>
                      )}
                      {poll.score !== null && (
                        <span className="text-gray-600 dark:text-gray-400">
                          {(poll.score * 100).toFixed(1)}%
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-500">
                      {getTimeSince(poll.polledAt)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
